import jsum from 'jsum'
import { FormTypes } from '@oneblink/types'
import makeIsShowing, { makeIsOptionShowing } from './is-element-showing'

interface HashKeySource {
  Originating?: string
  Via?: string
  Origin_Details?: string
  Final_Destination: string
  Entering_NSW: string
  Return_Qld_7_Days: string
  Self_Cert_Applied_Qualified: string
  Horses_21_Day_Treatment?: string
  One_Manual_Inspection: string
  Two_Clean_Treatments: string
  Risk_Mgmt_Plan_Origin?: string
  Risk_Mgmt_Plan_Destination?: string
  Dipping_Required: string
  Leave_NSW_Not_GT_7_Days: string
  Stabled_For_35_Days: string
  High_Risk_QLD: string
  Low_Risk_NSW: string
  Inspected_Free_Of_CT: string
  Registered_Treatment_Applied: string
}

const map = {
  Movement_Originating: 'Originating',
  Movement_Entering_NSW_Via: 'Via',
  Origin_Details: 'Origin_Details',
  Final_Destination: 'Final_Destination',
  Entering_NSW: 'Entering_NSW',
  Returning_7Days: 'Return_Qld_7_Days',
  Self_Certification: 'Self_Cert_Applied_Qualified',
  Horses_21Days: 'Horses_21_Day_Treatment',
  Clean_Examination: 'One_Manual_Inspection',
  Clean_Eradication: 'Two_Clean_Treatments',
  Risk_Mgmt_Plan_Origin: 'Risk_Mgmt_Plan_Origin',
  Risk_Mgmt_Plan_Destination: 'Risk_Mgmt_Plan_Destination',
  RHE_7_Day: 'Leave_NSW_Not_GT_7_Days',
  RHE_35_Day_Stabling: 'Stabled_For_35_Days',
  Self_Certification_High_Risk: 'High_Risk_QLD',
  Self_Certification_Low_Risk: 'Low_Risk_NSW',
  Self_Certification_Manually_Inspect: 'Inspected_Free_Of_CT',
  Self_Certification_Apply_Chemical: 'Registered_Treatment_Applied',
} as const

type FieldMap = typeof map
type FieldMapProp<T extends keyof FieldMap | undefined> =
  T extends keyof FieldMap ? keyof FieldMap : void

interface MeasuresForAnimals {
  animals: string[]
  value: string
}

export interface ProcessedMeasuresAndRequirements {
  mandatoryMeasures: MeasuresForAnimals[]
  preliminaryRequirements: MeasuresForAnimals[]
  finalRequirements: MeasuresForAnimals[]
}

function isMappedField<T extends string>(
  key: T,
): FieldMapProp<keyof FieldMap | undefined> {
  return Object.keys(map).indexOf(key) > -1
    ? (key as keyof FieldMap)
    : undefined
}

const destinationDetailsMap: Record<string, string> = {
  'Moving onto an INFESTED holding in NSW': 'To an INFESTED holding in NSW',
  'Moving onto an ADJOINING holding in NSW': 'To an ADJOINING holding in NSW',
  'Moving onto a non restricted property':
    'To a NON RESTRICTED PROPERTY in NSW',
}

function extractHashSource(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
): HashKeySource {
  const hashSource: Record<string, boolean | string | void> = {}

  const isShowing = makeIsShowing(submission, definition)

  for (const [key, value] of Object.entries(submission)) {
    const submissionFieldName = isMappedField(key)
    if (submissionFieldName) {
      if (isShowing(submissionFieldName)) {
        if (typeof value === 'string') {
          const targetPropName = map[submissionFieldName]
          let targetValue = value

          /* special cases - start */
          // Final_Destination
          if (targetValue) {
            if (
              submissionFieldName === 'Final_Destination' &&
              value === 'To a saleyard' &&
              (
                (submission.Destination_Saleyard as string) || ''
              ).toLowerCase() === 'other saleyard'
            ) {
              targetValue = 'To a saleyard (other)'
            }
            if (
              submissionFieldName === 'Final_Destination' &&
              value === 'To a property'
            ) {
              targetValue =
                destinationDetailsMap[
                  (submission.Destination_Details as string) || ''
                ]
            }
            // Origin_Details
            if (submissionFieldName === 'Movement_Originating') {
              if (
                [
                  'FROM the Magic Millions Sales Complex',
                  'FROM the Royal Brisbane Show (EKKA)',
                ].includes(targetValue)
              ) {
                hashSource.Origin_Details = targetValue
              }
            }
            /* special cases - end */
            hashSource[targetPropName] = targetValue
          }
        }
      }
    }
  }

  return hashSource as unknown as HashKeySource
}

const bitPositions = {
  Cattle: 0,
  Bison: 1,
  Buffalo: 2,
  Deer: 3,
  Horse: 4,
  Donkey: 5,
  Mule: 6,
  Goat: 7,
  Sheep: 8,
  Camel: 9,
  Llama: 10,
  Alpaca: 11,
  Guanaco: 12,
  Vicuña: 13,
} as const

export function getVisibleSubCarriers(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
): string[] {
  const species: string[] = []
  const carriersSub = (submission.Cattle_Tick_Carrier_Sub || []) as string[]
  const speciesEl =
    definition.elements[0].type === 'page'
      ? definition.elements[0].elements.find((el) =>
          'name' in el ? el.name === 'Cattle_Tick_Carrier_Sub' : false,
        )
      : undefined

  const isOptionShowing = makeIsOptionShowing({ submission, definition })

  if (speciesEl && speciesEl.type === 'checkboxes') {
    const speciesOptions = speciesEl.options || []
    speciesOptions.forEach((option) => {
      if (
        isOptionShowing({ option, element: speciesEl }) &&
        carriersSub.indexOf(option.value) > -1
      ) {
        species.push(option.value)
      }
    })
  }

  return species
}

function animalChecksum(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
) {
  const carriers = (submission.Cattle_Tick_Carrier || []) as string[]

  const species = getVisibleSubCarriers(submission, definition)

  const checksum = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]

  const allCarriers = carriers.concat(species) as unknown as Array<
    keyof typeof bitPositions
  >

  for (const animalName of allCarriers) {
    const position = Number.parseInt(bitPositions[animalName] + '')
    if (!isNaN(position)) {
      checksum[position] = 1
    }
  }

  return parseInt(checksum.join(''), 2)
}

export function findMeasures(
  submission: Record<string, unknown>,
  data: RequirementsAndMeasures,
  definition: FormTypes.Form,
) {
  if (data) {
    const hashSource = extractHashSource(submission, definition)
    const hashKey = jsum.digest(hashSource, 'sha256', 'hex')
    const rhsMatches = data[hashKey]
    if (!rhsMatches || !rhsMatches.length) {
      return []
    }

    const checksum = animalChecksum(submission, definition)

    return rhsMatches.filter((m) => m.bitComparator & checksum)
  }

  return []
}

type AnimalName = keyof RequirementsAndMeasuresItem

function isInMeasure(
  prop: string | AnimalName,
  measure: RequirementsAndMeasuresItem,
): prop is AnimalName {
  return prop in measure
}

function getAnimalsForRequirementsAndMeasures(
  measure: RequirementsAndMeasuresItem,
) {
  return Object.keys(bitPositions).reduce(
    (memo: string[], animalName: string) => {
      if (isInMeasure(animalName, measure) && measure[animalName]) {
        memo.push(animalName)
      }

      return memo
    },
    [],
  )
}

function groupMeasures(
  measures: RequirementsAndMeasuresItem[],
  selectedAnimals: string[],
) {
  return measures.reduce(
    (
      memo: {
        mandatoryMeasures: MeasuresForAnimals[]
        finalRequirements: MeasuresForAnimals[]
        preliminaryRequirements: MeasuresForAnimals[]
      },
      measure,
    ) => {
      const animals = getAnimalsForRequirementsAndMeasures(measure).filter(
        (animal) => selectedAnimals.indexOf(animal) !== -1,
      )

      measure.Mandatory_Measures &&
        memo.mandatoryMeasures.push({
          animals,
          value: measure.Mandatory_Measures,
        })
      measure.Final_Requirement &&
        memo.finalRequirements.push({
          animals,
          value: measure.Final_Requirement,
        })
      measure.Preliminary_Requirement &&
        memo.preliminaryRequirements.push({
          animals,
          value: measure.Preliminary_Requirement,
        })
      return memo
    },
    {
      mandatoryMeasures: [],
      finalRequirements: [],
      preliminaryRequirements: [],
    },
  )
}

const arrayUnion = (arr1: string[], arr2: string[]) => {
  return Array.from(new Set([...arr1, ...arr2]))
}

function dedupeRequirementsAndMeasures(data: MeasuresForAnimals[]) {
  const result: MeasuresForAnimals[] = []
  for (let i = 0; i < data.length; i++) {
    const measure = data[i].value
    const animals = [...data[i].animals]

    let idx = 0
    let newText = true
    for (; idx < result.length; idx++) {
      if (measure === result[idx].value) {
        newText = false
        break
      }
    }

    if (newText) {
      result.push({
        animals,
        value: measure,
      })
    } else {
      result[idx].animals = arrayUnion(result[idx].animals, animals)
    }
  }

  return result
}

export function processMeasures(
  measures: RequirementsAndMeasuresItem[],
  selectedAnimals: string[],
): ProcessedMeasuresAndRequirements {
  const groupedMeasures = groupMeasures(measures, selectedAnimals)

  const mandatoryMeasures = dedupeRequirementsAndMeasures(
    groupedMeasures.mandatoryMeasures,
  )
  const preliminaryRequirements = dedupeRequirementsAndMeasures(
    groupedMeasures.preliminaryRequirements,
  )
  const finalRequirements = dedupeRequirementsAndMeasures(
    groupedMeasures.finalRequirements,
  )

  return {
    mandatoryMeasures,
    preliminaryRequirements,
    finalRequirements,
  }
}

export function getMeasuresRequirementsHTML(
  requirementsMeasures: MeasuresForAnimals[],
) {
  let result = ``
  if (requirementsMeasures.length > 0) {
    requirementsMeasures.forEach((entry) => {
      result += `<strong>Animals: ${entry.animals.join(
        ', ',
      )}</strong> <br /> <br />
              <p>${entry.value}</p><br />
              <hr />`
    })
  } else {
    result = '<i>None</i>'
  }
  return result
}
